import React, { useState, useEffect, useMemo, useRef, ReactNode } from 'react';
import { Layout, Menu, Button, Dropdown, MenuProps, Drawer } from 'antd';
import { useWorkplaceStore } from '../stores/contexts/workplace';
import { IMenuItemModel } from '../models/imenu-item.model';
import { IWorkplaceDetailedDTO } from '@common-frontend-submodule/interfaces/workplace/iworkplace-detailed.interface';
import { ApartmentOutlined, AppstoreOutlined, BlockOutlined, BuildOutlined, CheckCircleOutlined, CommentOutlined, DesktopOutlined, FormOutlined, GlobalOutlined, HomeOutlined, MenuFoldOutlined, MenuUnfoldOutlined, ReloadOutlined, SolutionOutlined, TeamOutlined, UserOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import LocalStorage from '../plugins/localStorage';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../stores/contexts/auth';
import { useUserStore } from '../stores/contexts/user';
import { StyledMenu } from './styledComponents/StyledMenu';
import { StyledSideMenuSelect } from './styledComponents/StyledSideMenuSelect';
import { useChatStore } from '../stores/contexts/chat';
import { useSocket } from '../plugins/socket.io';
import { HeaderChatNotification } from './HeaderChatNotofocation';

const { Header, Content, Sider } = Layout;
const { Option } = StyledSideMenuSelect;

interface MenuSidebarProps {
  sidebar: boolean;
  toggleSidebar: () => void;
  children?: ReactNode;
  setRefreshKey: React.Dispatch<React.SetStateAction<number>>;
}

export const MenuSidebar: React.FC<MenuSidebarProps> = ({ sidebar, toggleSidebar, children, setRefreshKey }) => {
  const workplaceStore = useWorkplaceStore();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { currentUser } = useUserStore();
  const { availableWorkplaces, currentWorkplace, setCurrentWorkplace } = workplaceStore;
  const chatStore = useChatStore();
  const socket = useSocket();

  const sidebarRef = useRef<HTMLDivElement>(null);

  const [model, setModel] = useState<IWorkplaceDetailedDTO | null>(currentWorkplace);
  const [visible, setVisible] = useState(true);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleProfile = () => {
    navigate(`/user/profile/${currentUser?.id}`);
  };

  const handleLocaleChange = (locale: string) => {
    i18n.changeLanguage(locale);
    LocalStorage.set('locale', locale);
  };

  const localeMenu: MenuProps['items'] = [
    {
      key: 'en',
      label: (
        <Menu.Item key="en" onClick={() => handleLocaleChange('en')}>
          {i18n.t('English')}
        </Menu.Item>
      ),
    },
    {
      key: 'ru',
      label: (
        <Menu.Item key="ru" onClick={() => handleLocaleChange('ru')}>
          {i18n.t('Русский')}
        </Menu.Item>
      ),
    },
  ];

  const userMenu: MenuProps['items'] = [
    {
      key: 'profile',
      label: (
        <Menu.Item key="profile" onClick={handleProfile}>
          {i18n.t('profile')}
        </Menu.Item>
      ),
    },
    {
      key: 'exit',
      label: (
        <Menu.Item key="exit" onClick={handleLogout}>
          {i18n.t('exit')}
        </Menu.Item>
      ),
    },
  ];

  useEffect(() => {
    setModel(currentWorkplace);
  }, [currentWorkplace]);

  const handleWorkplaceChange = (value: string) => {
    const selectedWorkplace = availableWorkplaces.find((wp) => wp.id === value);
    if (selectedWorkplace) {
      setCurrentWorkplace(selectedWorkplace);
    }
  };

  const homeMenuItem: IMenuItemModel = {
    position: 0,
    name: 'home',
    icon: 'home',
    roles: [],
    menu: [],
    activeLink: '',
    route: '',
  };

  type MenuItem = Required<MenuProps>['items'][number];
  //TODO добавить иконки для всех существующих модулей
  const icons = {
    home: <HomeOutlined />,
    user: <UserOutlined />,
    role: <ApartmentOutlined />,
    permission: <CheckCircleOutlined />,
    workplace: <DesktopOutlined />,
    client: <TeamOutlined />,
    office: <BuildOutlined />,
    contract: <FormOutlined />,
    chat: <CommentOutlined />,
    assignment: <AppstoreOutlined />,
    application: <SolutionOutlined />,
  };

  const items: MenuItem[] = useMemo(() => {
    if (currentWorkplace && currentWorkplace.modules) {
      const preparedMenuItems = ([homeMenuItem, ...currentWorkplace.modules] as any)?.map((module) => {
        const name = module.name as string;

        return {
          key: module.route !== '' ? '/' + module.route : module.route,
          icon: icons[name.toLowerCase()] ?? <BlockOutlined />,
          label: i18n.t(name.toLowerCase()),
        };
      });

      return preparedMenuItems;
    } else {
      return [homeMenuItem];
    }
  }, [currentWorkplace, i18n.language]);

  // useEffect(() => {
  //   const handleClickOutside = (event: MouseEvent) => {
  //     if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node) && sidebar) {
  //       toggleSidebar();
  //     }
  //   };

  //   document.addEventListener('mousedown', handleClickOutside);
  //   return () => {
  //     document.removeEventListener('mousedown', handleClickOutside);
  //   };
  // }, [sidebar, toggleSidebar]);


  const getActiveMenuKey = (pathname: string) => {
    const activeItem = items.find((item) =>
      item?.key && pathname.startsWith(item?.key as string)
    );
    return activeItem ? (activeItem.key) as string : '';
  };

  const activeKey = getActiveMenuKey(location.pathname);

  const onClick: MenuProps['onClick'] = (e) => {
    navigate(e.key);
    if (!visible) {
      toggleSidebar()
    }
  };

  useEffect(() => {
    if (!socket) return

    socket.on("NEW_CHAT_MESSAGE", (event) => {
      const message = event

      chatStore.setUnreadMessages((prev) => {
        const currentCount = prev[message.chatId]?.count || 0;

        return {
          ...prev,
          [message.chatId]: {
            count: currentCount + 1,
            user: message.telegramUser,
          },
        };
      });
    });

    return () => {
      socket.off("NEW_CHAT_MESSAGE");
    };
  }, [socket]);

  return (
    <Layout style={{ height: '100vh' }}>
      <Sider
        trigger={null}
        collapsible
        breakpoint="md"
        collapsed={!sidebar}
        onCollapse={toggleSidebar}
        onBreakpoint={(broken) => {
          if (broken) setVisible(false);
        }}
        collapsedWidth={0}
        width={240}
        style={{
          display: !visible ? 'none' : 'block',
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          fontWeight: 'bold',
          left: 0,
          top: 0,
          bottom: 0,
          backgroundColor: '#121212',
          transition: 'all 0.2s',
        }}
        ref={sidebarRef}
      >
        {availableWorkplaces.length > 0 && (
          <StyledSideMenuSelect
            showSearch
            placeholder="Select a workplace"
            optionFilterProp="children"
            value={model?.id}
            onChange={(e) => handleWorkplaceChange(e as string)}
            style={{ width: '80%', margin: '16px', backgroundColor: '#121212', color: '#fff' }}
          >
            {availableWorkplaces?.map((workplace) => (
              <Option key={workplace.id} value={workplace.id}>
                {workplace.name}
              </Option>
            ))}
          </StyledSideMenuSelect>
        )}
        <StyledMenu
          mode="vertical"
          items={items}
          style={{ color: '#fff !important' }}
          onClick={onClick}
          selectedKeys={[activeKey]}
        />
      </Sider>

      {!visible &&
        <Drawer
          open={sidebar}
          onClose={toggleSidebar}
          closeIcon={<MenuUnfoldOutlined style={{ color: 'white' }} />}
          placement="left"
          key="left"
          bodyStyle={{ padding: 0 }}
          style={{ backgroundColor: '#121212',}}
        >
          {availableWorkplaces.length > 0 && (
            <StyledSideMenuSelect
              showSearch
              placeholder="Select a workplace"
              optionFilterProp="children"
              value={model?.id}
              onChange={(e) => handleWorkplaceChange(e as string)}
              style={{ width: '80%', margin: '16px', backgroundColor: '#121212', color: '#fff' }}
            >
              {availableWorkplaces?.map((workplace) => (
                <Option key={workplace.id} value={workplace.id}>
                  {workplace.name}
                </Option>
              ))}
            </StyledSideMenuSelect>
          )}
          <StyledMenu
            mode="vertical"
            items={items}
            style={{ color: '#fff !important' }}
            onClick={onClick}
            selectedKeys={[activeKey]}
          />
        </Drawer>
      }

      <Layout style={{ marginLeft: (sidebar && visible) ? 240 : 0, transition: 'margin-left 0.2s' }}>
        <Header style={{ padding: '0 16px', background: '#f4f7fa', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Button
              type="text"
              icon={sidebar ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={toggleSidebar}
              style={{ fontSize: '16px', color: '#121212' }}
            />
            <Button
              type="text"
              icon={<ReloadOutlined />}
              onClick={() => setRefreshKey((prevKey: number) => prevKey + 1)}
              style={{ fontSize: '16px', color: '#121212' }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <HeaderChatNotification unreadMessages={chatStore.unreadMessages} />
            <Dropdown menu={{ items: localeMenu }} trigger={['click']}>
              <Button
                type="text"
                icon={<GlobalOutlined />}
                style={{ fontSize: '16px', color: '#121212' }}
              >
                {i18n.language === 'en' ? 'English' : 'Русский'}
              </Button>
            </Dropdown>
            <Dropdown menu={{ items: userMenu }} trigger={['click']}>
              <Button
                type="text"
                icon={<UserOutlined />}
                style={{ fontSize: '16px', color: '#121212' }}
              >
                {visible && currentUser?.firstName} {(visible && currentUser?.lastName) || ''}
              </Button>
            </Dropdown>
          </div>
        </Header>
        <Content style={{ overflow: 'initial', background: '#f4f7fa' }}>
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};
